import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaCar,
  FaSave,
  FaTag,
  FaClock,
  FaMoneyBillWave,
  FaEdit,
  FaDollarSign,
  FaTrashAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaTimesCircle,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {
  additionalOptions as additionalOptionsApi,
  cars,
  oxygen,
} from "../../services/api";

// Utility functions for calculating rental duration and additional options pricing
const calculateRentalDays = (pickupDateTime, returnDateTime) => {
  const pickup = new Date(pickupDateTime);
  const returnDate = new Date(returnDateTime);
  const diffInMs = returnDate - pickup;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24)); // Round up to ensure minimum 1 day
  return Math.max(1, diffInDays); // Minimum 1 day
};

const calculateAdditionalOptionsPrice = (additionalOptions, rentalDays) => {
  if (!additionalOptions || additionalOptions.length === 0) return 0;
  return additionalOptions.reduce(
    (sum, option) => sum + option.price * rentalDays,
    0
  );
};

// Custom date input component for the DatePicker
const CustomDateTimeInput = React.forwardRef(
  ({ value, onClick, placeholder, isError }, ref) => (
    <motion.button
      type="button"
      style={{
        border: isError ? "1px solid #ef4444" : "1px solid #e5e7eb",
        color: isError ? "#ef4444" : "#374151",
      }}
      className={`w-full h-[42px] px-3 rounded text-left bg-white hover:border-blue-500 transition-colors duration-300 ${
        isError ? "bg-red-50" : ""
      }`}
      onClick={onClick}
      ref={ref}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {value || placeholder}
    </motion.button>
  )
);

const EditRentalModal = ({
  rental,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  isUpdating,
}) => {
  const [formData, setFormData] = useState({
    customer: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      age: "",
    },
    pickupLocation: "",
    returnLocation: "",
    pickupDateTime: null,
    returnDateTime: null,
    additionalNotes: "",
    couponCode: "",
    regenerateInvoice: true,
    additionalOptions: {},
    totalPrice: 0,
    basePrice: 0,
    customPricePerDay: null,
    durationInDays: 0,
    useCustomPricing: false,
    paymentMethod: "",
  }); // State for API loaded data
  const [additionalOptionsData, setAdditionalOptionsData] = useState([]);
  const [carData, setCarData] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  // We no longer need a separate state for seasonal pricing as we'll use carData.availability directly
  const [loading, setLoading] = useState({
    additionalOptions: false,
    carData: false,
    paymentMethods: false,
  });

  // Track if dates have changed to trigger price recalculation
  const [datesChanged, setDatesChanged] = useState(false);
  // Add a state for price preview
  const [pricePreview, setPricePreview] = useState(null);
  // Add date picker states
  const [datePickerOpen, setDatePickerOpen] = useState({
    pickup: false,
    dropoff: false,
  });

  const [dateTimeComplete, setDateTimeComplete] = useState({
    pickup: false,
    dropoff: false,
  });

  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false,
  });

  // Add new state for status and delete confirmation
  const [selectedStatus, setSelectedStatus] = useState("");
  const [cancellationReason, setCancellationReason] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add state to track original additional options pricing
  const [originalAdditionalOptionsPrice, setOriginalAdditionalOptionsPrice] =
    useState(0);

  // Add invoice state
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    companyName: "",
    vatNumber: "",
    address: "",
    city: "",
    postalCode: "",
    country: "Greece",
  });

  // Add form errors state for invoice validation
  const [formErrors, setFormErrors] = useState({});

  // Status options with their display properties
  const statusOptions = [
    {
      value: "pending",
      label: "Pending",
      color: "text-yellow-600",
      bgColor: "bg-yellow-50",
      icon: FaClock,
    },
    {
      value: "confirmed",
      label: "Confirmed",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      icon: FaCheckCircle,
    },
    {
      value: "completed",
      label: "Completed",
      color: "text-gray-600",
      bgColor: "bg-gray-50",
      icon: FaCheckCircle,
    },
    {
      value: "cancelled",
      label: "Cancelled",
      color: "text-red-600",
      bgColor: "bg-red-50",
      icon: FaTimesCircle,
    },
  ];

  // Function to render price below each day in the calendar
  const renderDayContents = (day, date) => {
    const formattedDate = date.toISOString().split("T")[0];

    // If we have seasonal pricing data, show the price for this day
    let dayPrice = null;

    if (
      carData &&
      carData.availability &&
      Array.isArray(carData.availability) &&
      carData.availability.length > 0
    ) {
      // Find the price for this day in the car's availability array
      // Note: date in availability is stored with time, so we need to compare just the date part
      // We need to convert both to dates without time for comparison
      const availabilityItem = carData.availability.find((item) => {
        if (!item || !item.date) return false;

        // Convert the item date to রাষ্ট্র-MM-DD format
        const itemDate = new Date(item.date);
        const itemDateStr = `${itemDate.getFullYear()}-${String(
          itemDate.getMonth() + 1
        ).padStart(2, "0")}-${String(itemDate.getDate()).padStart(2, "0")}`;

        // Convert the calendar date to রাষ্ট্র-MM-DD format
        const calDate = new Date(date);
        const calDateStr = `${calDate.getFullYear()}-${String(
          calDate.getMonth() + 1
        ).padStart(2, "0")}-${String(calDate.getDate()).padStart(2, "0")}`;

        return itemDateStr === calDateStr;
      });

      if (availabilityItem && availabilityItem.dynamicPrice) {
        dayPrice = availabilityItem.dynamicPrice;
        // For debugging - uncomment if needed
        console.log(`Found price for ${formattedDate}: ${dayPrice}`);
      }
    }

    // If no dynamic price found, use the base price
    if (!dayPrice && carData) {
      dayPrice = carData.basePrice;
    }

    // If we still don't have a price, use rental's base price
    if (!dayPrice && rental) {
      dayPrice = rental.basePrice || 0;
    }

    // Don't show 0 prices
    const displayPrice = dayPrice > 0 ? dayPrice : null;

    return (
      <div className="relative text-center">
        <div>{day}</div>
        {displayPrice && (
          <div
            className="text-[10px] bg-blue-100 text-blue-800 rounded-sm px-1 leading-tight mt-1"
            style={{ fontSize: "0.65rem" }}
          >
            €{displayPrice}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (rental) {
      // Convert array of additional options objects to a map of option ID -> true
      const additionalOptionsMap = {};
      if (rental.additionalOptions && Array.isArray(rental.additionalOptions)) {
        rental.additionalOptions.forEach((option) => {
          if (option._id) {
            additionalOptionsMap[option._id] = true;
          }
        });
      } else if (
        rental.additionalOptions &&
        typeof rental.additionalOptions === "object"
      ) {
        // Handle legacy format (the object format)
        Object.assign(additionalOptionsMap, rental.additionalOptions);
      }

      // Calculate original additional options price
      if (rental.additionalOptions && Array.isArray(rental.additionalOptions)) {
        const originalRentalDays = calculateRentalDays(
          rental.pickupDateTime,
          rental.returnDateTime
        );
        const originalOptionsPrice = calculateAdditionalOptionsPrice(
          rental.additionalOptions,
          originalRentalDays
        );
        setOriginalAdditionalOptionsPrice(originalOptionsPrice);
      }

      // Calculate duration between dates
      const pickupDate = new Date(rental.pickupDateTime);
      const returnDate = new Date(rental.returnDateTime);
      let calculatedDays = 1; // Default to 1 day
      if (returnDate > pickupDate) {
        const diffInMilliseconds = returnDate - pickupDate;
        const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
        calculatedDays = Math.ceil(diffInHours / 24);
      }
      setFormData({
        customer: {
          firstName: rental.customer.firstName,
          lastName: rental.customer.lastName,
          email: rental.customer.email,
          phoneNumber: rental.customer.phoneNumber,
          age: rental.customer.age,
        },
        pickupLocation: rental.pickupLocation,
        returnLocation: rental.returnLocation,
        pickupDateTime: new Date(rental.pickupDateTime),
        returnDateTime: new Date(rental.returnDateTime),
        additionalNotes: rental.additionalNotes || "",
        couponCode: rental.couponCode || "",
        regenerateInvoice: true,
        // Initialize with mapped additional options
        additionalOptions: additionalOptionsMap,
        totalPrice: rental.totalPrice || 0,
        basePrice: rental.basePrice || 0,
        customPricePerDay: rental.customPricePerDay || null,
        useCustomPricing: !!rental.customPricePerDay,
        durationInDays:
          rental.durationInDays ||
          calculatedDays ||
          (rental.priceBreakdown && rental.priceBreakdown.length > 0
            ? rental.priceBreakdown[0].numberOfDays
            : 1),
        paymentMethod: rental.paymentMethod || "",
      }); // Set the current status
      setSelectedStatus(rental.status || "pending");
      setCancellationReason(rental.cancellationReason || "");

      // Initialize invoice data
      if (rental.invoiceType === "invoice" && rental.invoiceData) {
        setWantsInvoice(true);
        setInvoiceData({
          companyName: rental.invoiceData.companyName || "",
          vatNumber: rental.invoiceData.vatNumber || "",
          address: rental.invoiceData.address || "",
          city: rental.invoiceData.city || "",
          postalCode: rental.invoiceData.postalCode || "",
          country: rental.invoiceData.country || "Greece",
        });
      } else {
        setWantsInvoice(false);
        setInvoiceData({
          companyName: "",
          vatNumber: "",
          address: "",
          city: "",
          postalCode: "",
          country: "Greece",
        });
      }

      setDatesChanged(false);
      setPricePreview(null);
    }
  }, [rental]);

  useEffect(() => {
    if (formData.pickupDateTime && formData.returnDateTime) {
      const pickupDate = new Date(formData.pickupDateTime);
      const returnDate = new Date(formData.returnDateTime);
      let days = 1; // Default to 1 day

      if (returnDate > pickupDate) {
        const diffInMilliseconds = returnDate - pickupDate;
        const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
        days = Math.ceil(diffInHours / 24);
      }
      days = Math.max(1, days); // Ensure duration is at least 1

      setFormData((prev) => ({
        ...prev,
        durationInDays: days,
      }));

      // Note: The specific price preview logic previously here when `datesChanged` was true
      // is now superseded by the more comprehensive `calculatePriceBasedOnAvailability`
      // which is called by other useEffects.
    }
  }, [formData.pickupDateTime, formData.returnDateTime]);

  const fetchAdditionalOptions = async () => {
    setLoading((prev) => ({ ...prev, additionalOptions: true }));
    try {
      const options = await additionalOptionsApi.getActive();
      if (options && Array.isArray(options)) {
        console.log("Fetched additional options:", options);
        setAdditionalOptionsData(options);
      }
    } catch (error) {
      console.error("Error fetching additional options:", error);
    } finally {
      setLoading((prev) => ({ ...prev, additionalOptions: false }));
    }
  };

  const fetchCarData = async (car) => {
    console.log("Fetching car data for ID:", car._id);
    if (!car._id) return;
    setLoading((prev) => ({ ...prev, carData: true }));
    try {
      const { data: carDetails } = await cars.getById(car._id);
      console.log("Fetched car data:", carDetails);
      setCarData(carDetails);
    } catch (error) {
      console.error("Error fetching car data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, carData: false }));
    }
  };

  const fetchPaymentMethods = async () => {
    setLoading((prev) => ({ ...prev, paymentMethods: true }));
    try {
      const response = await oxygen.getPaymentMethods();
      console.log("Fetching payment methods...: ", response);
      if (response && response.data && Array.isArray(response.data)) {
        console.log("Fetched payment methods:", response.data);
        setPaymentMethods(response.data);
      }
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    } finally {
      setLoading((prev) => ({ ...prev, paymentMethods: false }));
    }
  };

  useEffect(() => {
    if (isOpen && rental) {
      fetchAdditionalOptions();
      fetchPaymentMethods();
      if (rental.car) {
        fetchCarData(rental.car);
      }
    }
  }, [isOpen, rental]);

  const calculatePriceBasedOnAvailability = () => {
    if (
      !formData.pickupDateTime ||
      !formData.returnDateTime ||
      (!carData && !formData.useCustomPricing)
    ) {
      return null;
    }

    let rentalDuration = formData.durationInDays;
    if (
      (!rentalDuration || rentalDuration <= 0) &&
      formData.pickupDateTime &&
      formData.returnDateTime
    ) {
      const pickupMs = new Date(formData.pickupDateTime).getTime();
      const returnMs = new Date(formData.returnDateTime).getTime();
      if (returnMs > pickupMs) {
        rentalDuration = Math.ceil(
          (returnMs - pickupMs) / (1000 * 60 * 60 * 24)
        );
      } else {
        rentalDuration = 1;
      }
    }
    rentalDuration = Math.max(1, rentalDuration || 1);

    let carPriceForPeriod = 0;
    let carAverageDailyRate = 0;
    const carDailyPrices = [];
    let isCustom = formData.useCustomPricing;

    if (formData.useCustomPricing && formData.customPricePerDay !== null) {
      const customDailyRate = parseFloat(formData.customPricePerDay) || 0;
      carPriceForPeriod = customDailyRate * rentalDuration;
      carAverageDailyRate = customDailyRate;
      const currentDayIter = new Date(formData.pickupDateTime);
      for (let i = 0; i < rentalDuration; i++) {
        carDailyPrices.push({
          date: new Date(currentDayIter).toISOString().split("T")[0],
          price: customDailyRate,
        });
        currentDayIter.setDate(currentDayIter.getDate() + 1);
      }
    } else if (carData) {
      const pickup = new Date(formData.pickupDateTime);
      const currentDayIter = new Date(pickup);

      for (let i = 0; i < rentalDuration; i++) {
        const formattedDate = new Date(currentDayIter)
          .toISOString()
          .split("T")[0];
        let dayPrice = null;

        if (carData.availability && Array.isArray(carData.availability)) {
          const availabilityItem = carData.availability.find((item) => {
            if (!item || !item.date) return false;
            return (
              new Date(item.date).toISOString().split("T")[0] === formattedDate
            );
          });
          if (
            availabilityItem &&
            typeof availabilityItem.dynamicPrice === "number"
          ) {
            dayPrice = availabilityItem.dynamicPrice;
          }
        }

        if (dayPrice === null && typeof carData.basePrice === "number") {
          dayPrice = carData.basePrice;
        }
        if (
          dayPrice === null &&
          rental &&
          typeof rental.basePrice === "number"
        ) {
          dayPrice = rental.basePrice;
        }
        dayPrice = dayPrice === null ? carData.basePrice || 0 : dayPrice;

        carPriceForPeriod += dayPrice;
        carDailyPrices.push({ date: formattedDate, price: dayPrice });
        currentDayIter.setDate(currentDayIter.getDate() + 1);
      }
      carAverageDailyRate =
        rentalDuration > 0
          ? carPriceForPeriod / rentalDuration
          : carData.basePrice || 0;
    } else {
      return null;
    }

    let additionalOptionsCost = 0;
    if (
      formData.additionalOptions &&
      additionalOptionsData &&
      additionalOptionsData.length > 0
    ) {
      for (const optionId in formData.additionalOptions) {
        if (formData.additionalOptions[optionId]) {
          // If the option is selected
          const optionDetails = additionalOptionsData.find(
            (opt) => opt._id === optionId
          );
          if (optionDetails && typeof optionDetails.price === "number") {
            additionalOptionsCost += optionDetails.price * rentalDuration;
          }
        }
      }
    }

    const finalTotalPrice = carPriceForPeriod + additionalOptionsCost;

    return {
      basePrice: carPriceForPeriod,
      totalPrice: finalTotalPrice,
      pricePerDay: carAverageDailyRate,
      dailyPrices: carDailyPrices,
      isCustomPrice: isCustom,
      additionalOptionsTotalCost: additionalOptionsCost,
    };
  };

  useEffect(() => {
    if (
      carData &&
      rental &&
      formData.pickupDateTime &&
      formData.returnDateTime &&
      additionalOptionsData
    ) {
      console.log(
        "Car data/options loaded or relevant formData changed, updating pricing preview"
      );
      const pricing = calculatePriceBasedOnAvailability();
      if (pricing) {
        const originalDuration =
          rental.durationInDays > 0
            ? rental.durationInDays
            : (() => {
                const pickupDate = new Date(rental.pickupDateTime);
                const returnDate = new Date(rental.returnDateTime);
                const diffInMilliseconds = returnDate - pickupDate;
                const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
                return Math.max(1, Math.ceil(diffInHours / 24));
              })();

        // Calculate original total excluding original additional options, then add current additional options
        const originalBasePrice =
          (rental.totalPrice || 0) - originalAdditionalOptionsPrice;

        setPricePreview({
          durationInDays: formData.durationInDays || 1,
          originalDuration: originalDuration,
          originalPrice:
            originalBasePrice + (pricing.additionalOptionsTotalCost || 0), // Original base + current additional options
          originalBasePrice: originalBasePrice,
          originalAdditionalOptionsPrice: originalAdditionalOptionsPrice,
          dailyRate: pricing.pricePerDay,
          estimatedTotalPrice: pricing.totalPrice,
          basePrice: pricing.basePrice, // Car's total for the period
          difference:
            pricing.totalPrice -
            (originalBasePrice + (pricing.additionalOptionsTotalCost || 0)),
          isCustomPrice: pricing.isCustomPrice,
          dailyPrices: pricing.dailyPrices,
          additionalOptionsCost: pricing.additionalOptionsTotalCost,
        });
      }
    }
  }, [
    carData,
    rental,
    formData.pickupDateTime,
    formData.returnDateTime,
    formData.durationInDays,
    formData.useCustomPricing,
    formData.customPricePerDay,
    formData.additionalOptions,
    additionalOptionsData,
    originalAdditionalOptionsPrice,
  ]);

  useEffect(() => {
    if (
      datesChanged ||
      formData.useCustomPricing ||
      formData.additionalOptions
    ) {
      // also trigger if additional options change
      const pricing = calculatePriceBasedOnAvailability();
      if (pricing) {
        const originalDuration = rental
          ? rental.durationInDays > 0
            ? rental.durationInDays
            : (() => {
                const pickupDate = new Date(rental.pickupDateTime);
                const returnDate = new Date(rental.returnDateTime);
                const diffInMilliseconds = returnDate - pickupDate;
                const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
                return Math.max(1, Math.ceil(diffInHours / 24));
              })()
          : 1;

        // Calculate original total excluding original additional options, then add current additional options
        const originalBasePrice = rental
          ? (rental.totalPrice || 0) - originalAdditionalOptionsPrice
          : 0;

        setPricePreview({
          durationInDays: formData.durationInDays || 1,
          originalDuration: originalDuration,
          originalPrice:
            originalBasePrice + (pricing.additionalOptionsTotalCost || 0), // Original base + current additional options
          originalBasePrice: originalBasePrice,
          originalAdditionalOptionsPrice: originalAdditionalOptionsPrice,
          dailyRate: pricing.pricePerDay,
          estimatedTotalPrice: pricing.totalPrice,
          basePrice: pricing.basePrice,
          difference:
            pricing.totalPrice -
            (originalBasePrice + (pricing.additionalOptionsTotalCost || 0)),
          isCustomPrice: pricing.isCustomPrice,
          dailyPrices: pricing.dailyPrices,
          additionalOptionsCost: pricing.additionalOptionsTotalCost,
        });
      }
    }
  }, [
    formData.pickupDateTime,
    formData.returnDateTime,
    formData.useCustomPricing,
    formData.customPricePerDay,
    datesChanged,
    carData,
    rental,
    formData.additionalOptions,
    additionalOptionsData,
    formData.durationInDays,
    originalAdditionalOptionsPrice,
  ]);

  const toggleCustomPricing = () => {
    setFormData((prev) => ({
      ...prev,
      useCustomPricing: !prev.useCustomPricing,
      customPricePerDay: !prev.useCustomPricing ? prev.customPricePerDay : null, // Reset custom price if toggling off
    }));
    setDatesChanged(true); // Trigger price recalculation
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (name === "customPricePerDay") {
      setDatesChanged(true); // To trigger re-calculation
    }
  };

  const handleAdditionalOptionChange = (optionId) => {
    setFormData((prev) => ({
      ...prev,
      additionalOptions: {
        ...prev.additionalOptions,
        [optionId]: !prev.additionalOptions[optionId],
      },
    }));
    // No need to setDatesChanged(true) here, as formData.additionalOptions is in dependency array of pricing useEffect
  };
  

  const handleDateChange = (date, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: date,
    }));
    setDatesChanged(true);
  };

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (onDelete) {
      setIsDeleting(true);
      try {
        await onDelete(rental._id);
        setShowDeleteConfirmation(false);
        onClose();
      } catch (error) {
        console.error("Error deleting rental:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate invoice fields if invoice is required
    const newErrors = {};
    if (wantsInvoice) {
      if (!invoiceData.companyName.trim())
        newErrors.companyName = "Company name is required for invoice";
      if (!invoiceData.vatNumber.trim())
        newErrors.vatNumber = "VAT number is required for invoice";
      if (!invoiceData.address.trim())
        newErrors.address = "Address is required for invoice";
      if (!invoiceData.city.trim())
        newErrors.city = "City is required for invoice";
      if (!invoiceData.postalCode.trim())
        newErrors.postalCode = "Postal code is required for invoice";
    }

    // If there are validation errors, show them and return
    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    // Clear any previous errors
    setFormErrors({});

    const pricing = calculatePriceBasedOnAvailability();
    if (!pricing) {
      // Handle error: pricing could not be determined
      console.error("Error: Pricing could not be determined for submission.");
      // Potentially show an error to the user
      return;
    }

    const processedAdditionalOptions = [];
    if (additionalOptionsData.length > 0 && formData.additionalOptions) {
      additionalOptionsData.forEach((option) => {
        if (formData.additionalOptions[option._id]) {
          processedAdditionalOptions.push({
            _id: option._id,
            name: option.name,
            price: option.price,
          });
        }
      });
    }
    const updateData = {
      customer: formData.customer,
      pickupLocation: formData.pickupLocation,
      returnLocation: formData.returnLocation,
      pickupDateTime: formData.pickupDateTime.toISOString(),
      returnDateTime: formData.returnDateTime.toISOString(),
      additionalNotes: formData.additionalNotes,
      couponCode: formData.couponCode,
      additionalOptions: processedAdditionalOptions,
      regenerateInvoice: formData.regenerateInvoice,
      durationInDays: formData.durationInDays || 1,
      totalPrice: pricing.totalPrice, // Grand total
      // basePrice here refers to the average daily rate of the car or custom daily rate
      basePrice: formData.useCustomPricing
        ? parseFloat(formData.customPricePerDay)
        : pricing.pricePerDay,
      customPricePerDay: formData.useCustomPricing
        ? parseFloat(formData.customPricePerDay)
        : null,
      useCustomPricing: formData.useCustomPricing,
      priceBreakdown: [],
      status: selectedStatus, // Ensure status is included
      cancellationReason: selectedStatus === "cancelled" ? cancellationReason : undefined,
      paymentMethod: formData.paymentMethod,
      invoiceType: wantsInvoice ? "invoice" : "receipt",
      invoiceData: wantsInvoice ? invoiceData : undefined,
    };

    if (formData.useCustomPricing && formData.customPricePerDay !== null) {
      updateData.priceBreakdown = [
        {
          startDate: formData.pickupDateTime.toISOString(),
          endDate: formData.returnDateTime.toISOString(),
          pricePerDay: parseFloat(formData.customPricePerDay),
          numberOfDays: formData.durationInDays || 1,
          totalForPeriod: pricing.basePrice, // Car price for period
          isCustomPrice: true,
        },
      ];
    } else if (pricing.dailyPrices && pricing.dailyPrices.length > 0) {
      const groupedPrices = [];
      if (
        pricing.dailyPrices.length === 0 &&
        (formData.durationInDays || 1) > 0
      ) {
        groupedPrices.push({
          startDate: formData.pickupDateTime.toISOString(),
          endDate: formData.returnDateTime.toISOString(),
          pricePerDay: pricing.pricePerDay,
          numberOfDays: formData.durationInDays || 1,
          totalForPeriod: pricing.basePrice, // Car price for period
        });
      } else {
        let currentGroup = {
          startDate: new Date(pricing.dailyPrices[0].date),
          price: pricing.dailyPrices[0].price,
          days: 0,
        };
        pricing.dailyPrices.forEach((day, index) => {
          if (day.price === currentGroup.price) {
            currentGroup.days++;
          } else {
            const groupEndDate = new Date(currentGroup.startDate);
            groupEndDate.setDate(
              groupEndDate.getDate() + currentGroup.days - 1
            );
            groupedPrices.push({
              startDate: currentGroup.startDate.toISOString(),
              endDate: groupEndDate.toISOString(),
              pricePerDay: currentGroup.price,
              numberOfDays: currentGroup.days,
              totalForPeriod: currentGroup.price * currentGroup.days,
            });
            currentGroup = {
              startDate: new Date(day.date),
              price: day.price,
              days: 1,
            };
          }
        });
        if (currentGroup.days > 0) {
          const groupEndDate = new Date(currentGroup.startDate);
          groupEndDate.setDate(groupEndDate.getDate() + currentGroup.days - 1);
          groupedPrices.push({
            startDate: currentGroup.startDate.toISOString(),
            endDate: groupEndDate.toISOString(),
            pricePerDay: currentGroup.price,
            numberOfDays: currentGroup.days,
            totalForPeriod: currentGroup.price * currentGroup.days,
          });
        }
      }
      updateData.priceBreakdown =
        groupedPrices.length > 0
          ? groupedPrices
          : [
              {
                startDate: formData.pickupDateTime.toISOString(),
                endDate: formData.returnDateTime.toISOString(),
                pricePerDay: pricing.pricePerDay,
                numberOfDays: formData.durationInDays || 1,
                totalForPeriod: pricing.basePrice, // Car price for period
              },
            ];
    } else {
      // Fallback if no daily prices (e.g., custom pricing was true but no price entered, then switched off)
      updateData.priceBreakdown = [
        {
          startDate: formData.pickupDateTime.toISOString(),
          endDate: formData.returnDateTime.toISOString(),
          pricePerDay: pricing.pricePerDay, // car average daily rate
          numberOfDays: formData.durationInDays || 1,
          totalForPeriod: pricing.basePrice, // car price for period
        },
      ];
    }
    console.log("Submitting rental update with status:", selectedStatus); // Debug log
    onUpdate(updateData);
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalVariants = {
    hidden: { opacity: 0, y: -50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", duration: 0.5 },
    },
    exit: { opacity: 0, y: 50, scale: 0.95, transition: { duration: 0.2 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div
            className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex justify-between items-center p-6 border-b border-gray-200 bg-white">
              <h2 className="text-2xl font-bold text-gray-900">Edit Rental</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-500 rounded-full hover:bg-gray-100"
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Add Status Selection Section */}
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FaInfoCircle className="mr-2 text-blue-600" />
                  Rental Status
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {statusOptions.map((status) => {
                    const IconComponent = status.icon;
                    const isSelected = selectedStatus === status.value;
                    return (
                      <button
                        key={status.value}
                        type="button"
                        onClick={() => handleStatusChange(status.value)}
                        className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                          isSelected
                            ? `${status.bgColor} border-current ${status.color} shadow-md`
                            : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex flex-col items-center">
                          <IconComponent
                            className={`w-5 h-5 mb-1 ${
                              isSelected ? status.color : "text-gray-400"
                            }`}
                          />
                          <span
                            className={`text-sm font-medium ${
                              isSelected ? status.color : "text-gray-600"
                            }`}
                          >
                            {status.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>{" "}
                {selectedStatus !== rental?.status && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                    <FaInfoCircle className="inline mr-1" />
                    Status will be changed from "
                    {
                      statusOptions.find((s) => s.value === rental?.status)
                        ?.label
                    }
                    " to "
                    {
                      statusOptions.find((s) => s.value === selectedStatus)
                        ?.label
                    }
                    "
                  </div>
                )}
              </div>

              {/* Cancellation Reason Section - Show only when status is cancelled */}
              {selectedStatus === "cancelled" && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <FaExclamationTriangle className="mr-2 text-red-600" />
                    Cancellation Reason
                  </h3>
                  <textarea
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder="Please provide a reason for cancellation..."
                    rows={3}
                    className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    required={selectedStatus === "cancelled"}
                  />
                  {!cancellationReason && selectedStatus === "cancelled" && (
                    <p className="mt-1 text-sm text-red-600">
                      A cancellation reason is required when cancelling a rental.
                    </p>
                  )}
                </div>
              )}

              {/* Payment Method Selection Section */}
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <FaMoneyBillWave className="mr-2 text-blue-600" />
                  Payment Method
                </h3>
                {loading.paymentMethods ? (
                  <div className="flex justify-center py-4">
                    <LoadingSpinner size="small" />
                  </div>
                ) : (
                  <div>
                    <select
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select payment method</option>
                      {paymentMethods.map((method) => (
                        <option key={method.title_en} value={method.id}>
                          {method.title_en} ({method.title_gr})
                        </option>
                      ))}
                    </select>
                    {formData.paymentMethod !== rental?.paymentMethod && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                        <FaInfoCircle className="inline mr-1" />
                        Payment method will be updated
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Existing car info section */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-md flex items-center">
                <div className="text-blue-700 mr-3">
                  <FaCar size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-800">
                    {rental?.car?.make} {rental?.car?.model}
                  </h3>
                  <p className="text-blue-600 text-sm">
                    {rental?.car?.year} • {rental?.car?.color} • Base Rate: €
                    {(pricePreview?.dailyRate !== undefined
                      ? pricePreview.dailyRate
                      : rental?.basePrice || carData?.basePrice
                    )?.toFixed(2) || "N/A"}
                    /day
                  </p>
                </div>
              </div>

              {/* Existing form sections */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                <div className="space-y-6">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <FaCalendarAlt className="mr-2 text-blue-600" />
                      Rental Period
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FaCalendarAlt className="inline mr-1 text-green-600" />{" "}
                            Pickup Date & Time
                          </label>{" "}
                          <DatePicker
                            selected={formData.pickupDateTime}
                            onChange={(date) =>
                              handleDateChange(date, "pickupDateTime")
                            }
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            customInput={
                              <CustomDateTimeInput placeholder="Select pickup date and time" />
                            }
                            required
                            renderDayContents={renderDayContents}
                            open={datePickerOpen.pickup}
                            onClickOutside={() => {
                              setDatePickerOpen((prev) => ({
                                ...prev,
                                pickup: false,
                              }));
                            }}
                            onInputClick={() =>
                              setDatePickerOpen({
                                pickup: true,
                                dropoff: false,
                              })
                            }
                            popperProps={{
                              strategy: "fixed",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: {
                                    rootBoundary: "viewport",
                                    padding: 8,
                                  },
                                },
                              ],
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FaCalendarAlt className="inline mr-1 text-red-600" />{" "}
                            Return Date & Time
                          </label>{" "}
                          <DatePicker
                            selected={formData.returnDateTime}
                            onChange={(date) =>
                              handleDateChange(date, "returnDateTime")
                            }
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            customInput={
                              <CustomDateTimeInput placeholder="Select return date and time" />
                            }
                            required
                            minDate={formData.pickupDateTime}
                            renderDayContents={renderDayContents}
                            open={datePickerOpen.dropoff}
                            onClickOutside={() => {
                              setDatePickerOpen((prev) => ({
                                ...prev,
                                dropoff: false,
                              }));
                            }}
                            onInputClick={() =>
                              setDatePickerOpen({
                                pickup: false,
                                dropoff: true,
                              })
                            }
                            popperProps={{
                              strategy: "fixed",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: {
                                    rootBoundary: "viewport",
                                    padding: 8,
                                  },
                                },
                              ],
                            }}
                          />
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-md border border-gray-200">
                        <div className="flex justify-between items-center">
                          <label className="text-sm font-medium text-gray-700 flex items-center">
                            <FaClock className="mr-1 text-blue-600" /> Rental
                            Duration:
                          </label>
                          <span className="text-lg font-semibold text-blue-700">
                            {formData.durationInDays || 1}{" "}
                            {(formData.durationInDays || 1) === 1
                              ? "day"
                              : "days"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <FaMapMarkerAlt className="mr-2 text-blue-600" />
                      Locations
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <FaMapMarkerAlt className="inline mr-1 text-green-600" />{" "}
                          Pickup Location
                        </label>
                        <input
                          type="text"
                          name="pickupLocation"
                          value={formData.pickupLocation}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <FaMapMarkerAlt className="inline mr-1 text-red-600" />{" "}
                          Return Location
                        </label>
                        <input
                          type="text"
                          name="returnLocation"
                          value={formData.returnLocation}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                                        {/* Invoice Type Selection */}
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center">
                          <FaFileInvoiceDollar className="mr-2 text-blue-600" />
                          Invoice Type
                        </h3>
                        <div className="flex items-center space-x-6 mt-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="invoiceType"
                              value="receipt"
                              checked={!wantsInvoice}
                              onChange={() => setWantsInvoice(false)}
                              className="mr-2 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Receipt</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="invoiceType"
                              value="invoice"
                              checked={wantsInvoice}
                              onChange={() => setWantsInvoice(true)}
                              className="mr-2 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Invoice</span>
                          </label>
                        </div>

                        {/* Invoice Fields */}
                        {wantsInvoice && (
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                            <h4 className="text-md font-medium text-gray-800 mb-3">Invoice Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                                  Company Name *
                                </label>
                                <input
                                  type="text"
                                  id="companyName"
                                  value={invoiceData.companyName}
                                  onChange={(e) => {
                                    setInvoiceData(prev => ({ ...prev, companyName: e.target.value }));
                                    if (formErrors.companyName) {
                                      setFormErrors(prev => ({ ...prev, companyName: undefined }));
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    formErrors.companyName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="Enter company name"
                                />
                                {formErrors.companyName && (
                                  <p className="mt-1 text-sm text-red-600">{formErrors.companyName}</p>
                                )}
                              </div>

                              <div>
                                <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                  VAT Number *
                                </label>
                                <input
                                  type="text"
                                  id="vatNumber"
                                  value={invoiceData.vatNumber}
                                  onChange={(e) => {
                                    setInvoiceData(prev => ({ ...prev, vatNumber: e.target.value }));
                                    if (formErrors.vatNumber) {
                                      setFormErrors(prev => ({ ...prev, vatNumber: undefined }));
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    formErrors.vatNumber ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="Enter VAT number"
                                />
                                {formErrors.vatNumber && (
                                  <p className="mt-1 text-sm text-red-600">{formErrors.vatNumber}</p>
                                )}
                              </div>

                              <div className="md:col-span-2">
                                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                  Address *
                                </label>
                                <input
                                  type="text"
                                  id="address"
                                  value={invoiceData.address}
                                  onChange={(e) => {
                                    setInvoiceData(prev => ({ ...prev, address: e.target.value }));
                                    if (formErrors.address) {
                                      setFormErrors(prev => ({ ...prev, address: undefined }));
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    formErrors.address ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="Enter address"
                                />
                                {formErrors.address && (
                                  <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>
                                )}
                              </div>

                              <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                  City *
                                </label>
                                <input
                                  type="text"
                                  id="city"
                                  value={invoiceData.city}
                                  onChange={(e) => {
                                    setInvoiceData(prev => ({ ...prev, city: e.target.value }));
                                    if (formErrors.city) {
                                      setFormErrors(prev => ({ ...prev, city: undefined }));
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    formErrors.city ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="Enter city"
                                />
                                {formErrors.city && (
                                  <p className="mt-1 text-sm text-red-600">{formErrors.city}</p>
                                )}
                              </div>

                              <div>
                                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                                  Postal Code *
                                </label>
                                <input
                                  type="text"
                                  id="postalCode"
                                  value={invoiceData.postalCode}
                                  onChange={(e) => {
                                    setInvoiceData(prev => ({ ...prev, postalCode: e.target.value }));
                                    if (formErrors.postalCode) {
                                      setFormErrors(prev => ({ ...prev, postalCode: undefined }));
                                    }
                                  }}
                                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    formErrors.postalCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                  }`}
                                  placeholder="Enter postal code"
                                />
                                {formErrors.postalCode && (
                                  <p className="mt-1 text-sm text-red-600">{formErrors.postalCode}</p>
                                )}
                              </div>

                              <div className="md:col-span-2">
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                                  Country
                                </label>
                                <input
                                  type="text"
                                  id="country"
                                  value={invoiceData.country}
                                  onChange={(e) => setInvoiceData(prev => ({ ...prev, country: e.target.value }))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Enter country"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <FaUser className="mr-2 text-blue-600" />
                      Customer Information
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            First Name
                          </label>
                          <input
                            type="text"
                            name="customer.firstName"
                            value={formData.customer.firstName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name
                          </label>
                          <input
                            type="text"
                            name="customer.lastName"
                            value={formData.customer.lastName}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <FaEnvelope className="inline mr-1 text-blue-600" />{" "}
                          Email
                        </label>
                        <input
                          type="email"
                          name="customer.email"
                          value={formData.customer.email}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FaPhone className="inline mr-1 text-blue-600" />{" "}
                            Phone Number
                          </label>
                          <input
                            type="text"
                            name="customer.phoneNumber"
                            value={formData.customer.phoneNumber}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outli</label>ne-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <FaIdCard className="inline mr-1 text-blue-600" />{" "}
                            Age
                          </label>
                          {console.log(rental.customer)}
                                  <input
                                  name="customer.age"
                                  value={formData.customer.age}
                                  defaultValue={rental.customer.age === '26+' ? 26 : rental.customer.age}
                                  onChange={handleChange}
                                  min="18"
                                  max="99"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                  />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <FaTag className="mr-2 text-blue-600" />
                      Additional Options & Information
                    </h3>
                    <div className="space-y-4">
                      <div className="bg-white p-3 rounded-md border border-gray-200">
                        <h4 className="font-medium text-gray-700 mb-2">
                          Additional Options
                        </h4>
                        {loading.additionalOptions ? (
                          <div className="flex justify-center py-4">
                            <LoadingSpinner size="small" />
                          </div>
                        ) : additionalOptionsData.length > 0 ? (
                          <div className="space-y-2">
                            {additionalOptionsData.map((option) => (
                              <div
                                key={option._id}
                                className="flex items-center justify-between border-b border-gray-100 pb-2"
                              >
                                <div className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={`option-${option._id}`}
                                    checked={
                                      !!formData.additionalOptions[option._id]
                                    }
                                    onChange={() =>
                                      handleAdditionalOptionChange(option._id)
                                    }
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <div className="ml-2">
                                    <label
                                      htmlFor={`option-${option._id}`}
                                      className="block text-sm font-medium text-gray-700"
                                    >
                                      {option.name}
                                    </label>
                                    {option.description && (
                                      <p className="text-xs text-gray-500">
                                        {option.description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {option.price > 0 && (
                                  <span className="text-sm font-medium text-blue-600">
                                    €{option.price.toFixed(2)}/day
                                  </span>
                                )}
                              </div>
                            ))}
                            {rental?.additionalOptions &&
                              rental.additionalOptions.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <p className="text-xs text-gray-600 mb-2">
                                    Original booking additional options:
                                  </p>
                                  <div className="space-y-1">
                                    {rental.additionalOptions.map(
                                      (option, index) => {
                                        const rentalDays = calculateRentalDays(
                                          rental.pickupDateTime,
                                          rental.returnDateTime
                                        );
                                        return (
                                          <span
                                            key={index}
                                            className="inline-flex items-center px-2 py-1 mr-2 mb-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                          >
                                            {option.name} - €
                                            {option.price.toFixed(2)}/day (€
                                            {(
                                              option.price * rentalDays
                                            ).toFixed(2)}{" "}
                                            total)
                                          </span>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500 py-2">
                            No additional options available
                          </div>
                        )}
                      </div>{" "}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Additional Notes
                        </label>
                        <textarea
                          name="additionalNotes"
                          value={formData.additionalNotes}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md shadow">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <FaDollarSign className="mr-2 text-blue-600" /> Pricing
                    Options
                  </h3>
                  <div className="flex items-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.useCustomPricing}
                        onChange={toggleCustomPricing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        Use Custom Price
                      </span>
                    </label>
                  </div>
                </div>

                {formData.useCustomPricing ? (
                  <div className="bg-white p-4 rounded-md border border-blue-100">
                    <div className="flex items-center">
                      <label className="block text-sm font-medium text-gray-700 mr-4">
                        Custom Price per Day (€):
                      </label>
                      <input
                        type="number"
                        name="customPricePerDay"
                        value={formData.customPricePerDay || ""}
                        onChange={handleChange}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </div>
                    {pricePreview && (
                      <div className="mt-4 pt-4 border-t border-blue-100">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-semibold text-gray-800 mb-2">
                              Price Calculation
                            </h4>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Custom Daily Rate:
                                </span>
                                <span className="font-medium">
                                  €
                                  {parseFloat(
                                    formData.customPricePerDay || 0
                                  ).toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Duration:
                                </span>
                                <span className="font-medium">
                                  {pricePreview.durationInDays} days
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Car Rental Cost:
                                </span>
                                <span className="font-medium">
                                  €{pricePreview.basePrice?.toFixed(2)}
                                </span>
                              </div>
                              {pricePreview.additionalOptionsCost > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Additional Options:
                                  </span>
                                  <span className="font-medium">
                                    €
                                    {pricePreview.additionalOptionsCost?.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between font-bold">
                                <span className="text-sm text-gray-600">
                                  Total (incl. 24% VAT):
                                </span>
                                <span className="text-blue-700">
                                  €
                                  {pricePreview.estimatedTotalPrice?.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="border-l border-blue-100 pl-4">
                            <h4 className="font-semibold text-gray-800 mb-2">
                              Comparison
                            </h4>
                            <div className="space-y-2">
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Original Base Price:
                                </span>
                                <span className="font-medium">
                                  €
                                  {(
                                    pricePreview.originalBasePrice || 0
                                  ).toFixed(2)}
                                </span>
                              </div>
                              {pricePreview.originalAdditionalOptionsPrice >
                                0 && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-600">
                                    Original Add-ons:
                                  </span>
                                  <span className="font-medium">
                                    €
                                    {pricePreview.originalAdditionalOptionsPrice.toFixed(
                                      2
                                    )}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  New Total:
                                </span>
                                <span className="font-medium">
                                  €
                                  {pricePreview.estimatedTotalPrice?.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Price Difference:
                                </span>
                                <span
                                  className={`font-medium ${
                                    pricePreview.difference >= 0
                                      ? "text-red-600"
                                      : "text-green-600"
                                  }`}
                                >
                                  {pricePreview.difference >= 0 ? "+" : ""}
                                  {pricePreview.difference?.toFixed(2)}€
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white p-4 rounded-md border border-blue-100">
                    <h4 className="font-medium text-gray-800 mb-2">
                      Seasonal Pricing
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Using dynamic prices from the car's availability calendar.
                    </p>
                    {pricePreview && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:border-r md:border-blue-100 md:pr-4">
                          <h5 className="font-semibold text-gray-800 mb-2">
                            Original Booking
                          </h5>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Duration:
                              </span>
                              <span className="font-medium">
                                {pricePreview.originalDuration} days
                              </span>
                            </div>
                            {/* additional options */}
                            {rental?.additionalOptions && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Additional Options:
                                </span>
                                <span className="font-medium">
                                  €
                                  {(
                                    rental?.additionalOptionsPrice || 0
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Total Price:
                              </span>
                              <span className="font-medium">
                                €
                                {(rental?.additionalOptionsPrice
                                  ? rental.totalPrice -
                                    rental.additionalOptionsPrice +
                                    (pricePreview.additionalOptionsCost || 0)
                                  : rental?.totalPrice || 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-semibold text-gray-800 mb-2">
                            New Booking Calculation
                          </h5>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Duration:
                              </span>
                              <span className="font-medium">
                                {pricePreview.durationInDays} days
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Avg. Daily Car Rate:
                              </span>
                              <span className="font-medium">
                                €{pricePreview.dailyRate?.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Car Rental Cost:
                              </span>
                              <span className="font-medium">
                                €{pricePreview.basePrice?.toFixed(2)}
                              </span>
                            </div>
                            {pricePreview.additionalOptionsCost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-600">
                                  Additional Options:
                                </span>
                                <span className="font-medium">
                                  €
                                  {pricePreview.additionalOptionsCost?.toFixed(
                                    2
                                  )}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between font-bold">
                              <span className="text-sm text-gray-600">
                                New Total Price:
                              </span>
                              <span className="text-blue-700">
                                €{pricePreview.estimatedTotalPrice?.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">
                                Price Difference:
                              </span>
                              <span
                                className={`font-medium ${
                                  pricePreview.difference >= 0
                                    ? "text-red-600"
                                    : "text-green-600"
                                }`}
                              >
                                {pricePreview.difference >= 0 ? "+" : ""}
                                {pricePreview.difference?.toFixed(2)}€
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 -mx-6 px-6 py-4 mt-6 border-t border-gray-200">
                <div className="flex justify-end items-center">
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        isUpdating ||
                        !formData.pickupDateTime ||
                        !formData.returnDateTime
                      }
                      className="inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {isUpdating ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Updating...</span>
                        </>
                      ) : (
                        <>
                          <FaSave className="mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditRentalModal;
